import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Video, Play, Square, Archive, Trash2, Users, Clock, Radio } from 'lucide-react';

export const LiveStreamPage = () => {
  const { api, user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [archivedStreams, setArchivedStreams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStreamOpen, setNewStreamOpen] = useState(false);
  const [streamForm, setStreamForm] = useState({ title: '', description: '', game_id: '' });
  const [activeStream, setActiveStream] = useState(null);

  const fetchData = async () => {
    try {
      const [liveRes, archivedRes, gamesRes] = await Promise.all([
        api().get('/livestreams?status=live'),
        api().get('/livestreams?status=archived'),
        api().get('/games')
      ]);
      setStreams(liveRes.data);
      setArchivedStreams(archivedRes.data);
      setGames(gamesRes.data.filter(g => g.status === 'in_progress' || g.status === 'scheduled'));
    } catch (error) {
      console.error('Failed to fetch streams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [api]);

  const handleStartStream = async () => {
    if (!streamForm.title) {
      toast.error('Title is required');
      return;
    }
    
    try {
      const response = await api().post('/livestreams', streamForm);
      setActiveStream(response.data);
      toast.success('Stream started!');
      setNewStreamOpen(false);
      setStreamForm({ title: '', description: '', game_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to start stream');
    }
  };

  const handleEndStream = async (streamId, saveArchive = true) => {
    try {
      await api().post(`/livestreams/${streamId}/end?save_archive=${saveArchive}`);
      toast.success(saveArchive ? 'Stream archived' : 'Stream ended');
      setActiveStream(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to end stream');
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Delete this stream archive?')) return;
    
    try {
      await api().delete(`/livestreams/${streamId}`);
      toast.success('Stream deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete stream');
    }
  };

  const handleJoinStream = async (streamId) => {
    try {
      await api().post(`/livestreams/${streamId}/join`);
      toast.success('Joined stream');
    } catch (error) {
      toast.error('Failed to join stream');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="livestream-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Live & Podcast</h1>
          <p className="text-muted-foreground mt-1">Watch live games and archived content</p>
        </div>
        {isAdmin && (
          <Dialog open={newStreamOpen} onOpenChange={setNewStreamOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="go-live-btn"
                className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded-sm"
              >
                <Radio className="w-4 h-4 mr-2 animate-pulse" />
                Go Live
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Start Live Stream</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Stream Title</label>
                  <Input
                    value={streamForm.title}
                    onChange={(e) => setStreamForm({ ...streamForm, title: e.target.value })}
                    placeholder="Game Day Live Commentary"
                    data-testid="stream-title-input"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description</label>
                  <Textarea
                    value={streamForm.description}
                    onChange={(e) => setStreamForm({ ...streamForm, description: e.target.value })}
                    placeholder="What's this stream about?"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Link to Game (Optional)</label>
                  <Select 
                    value={streamForm.game_id} 
                    onValueChange={(v) => setStreamForm({ ...streamForm, game_id: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Select a game" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No game</SelectItem>
                      {games.map((game) => (
                        <SelectItem key={game.id} value={game.id}>
                          {game.tournament_event} - {game.division}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleStartStream}
                  data-testid="start-stream-btn"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Stream
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Active Admin Stream Controls */}
      {activeStream && isAdmin && (
        <div className="bg-red-900/20 border border-red-900 p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <div>
                <p className="font-heading font-bold text-lg">{activeStream.title}</p>
                <p className="text-sm text-zinc-400">You are currently live</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleEndStream(activeStream.id, true)}
                data-testid="save-archive-btn"
                className="bg-zinc-800 hover:bg-zinc-700"
              >
                <Archive className="w-4 h-4 mr-2" />
                Save & End
              </Button>
              <Button
                onClick={() => handleEndStream(activeStream.id, false)}
                data-testid="end-stream-btn"
                className="bg-red-600 hover:bg-red-700"
              >
                <Square className="w-4 h-4 mr-2" />
                End Stream
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Live Streams */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          Live Now
        </h2>
        
        {streams.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
            <Video className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No live streams right now</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {streams.map((stream) => (
              <div
                key={stream.id}
                data-testid={`live-stream-${stream.id}`}
                className="bg-zinc-900/50 border border-zinc-800 overflow-hidden hover:border-red-900 transition-colors group"
              >
                <div className="aspect-video bg-zinc-800 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white/50 group-hover:text-white transition-colors" />
                  </div>
                  <div className="absolute top-2 left-2 px-2 py-1 bg-red-600 text-xs font-bold uppercase rounded-sm flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    LIVE
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-xs rounded-sm flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {stream.viewers?.length || 0}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold">{stream.title}</h3>
                  <p className="text-sm text-zinc-400">{stream.host_name}</p>
                  <Button
                    onClick={() => handleJoinStream(stream.id)}
                    data-testid={`join-stream-${stream.id}`}
                    className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-sm"
                  >
                    Watch Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Archived Streams */}
      <div>
        <h2 className="text-xl font-heading font-bold uppercase tracking-wide mb-4 flex items-center gap-2">
          <Archive className="w-5 h-5 text-zinc-500" />
          Archives
        </h2>
        
        {archivedStreams.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 p-12 text-center">
            <Archive className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">No archived streams yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {archivedStreams.map((stream) => (
              <div
                key={stream.id}
                data-testid={`archived-stream-${stream.id}`}
                className="bg-zinc-900/50 border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors"
              >
                <div className="aspect-video bg-zinc-800 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white/50" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold">{stream.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-zinc-400 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(stream.created_at)}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="flex-1 border-zinc-700 text-sm"
                    >
                      <Play className="w-4 h-4 mr-1" /> Watch
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        onClick={() => handleDeleteStream(stream.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
